class MobileMenu extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.openTrigger = null;
    this.headerColorResetTimeout = null;
    this.previousOverflow = '';

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onAccordionTriggerClick = this.onAccordionTriggerClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);

    // Must match the breakpoint where header.css hides the burger (.btn--burger).
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
  }

  connectedCallback() {
    this.drawer = this.querySelector('.mobile-menu__drawer');
    this.overlay = this.querySelector('.mobile-menu__overlay');
    this.header = document.querySelector('sticky-header');
    this.closeTriggers = this.querySelectorAll('[data-mobile-menu-close]');
    this.openTriggers = document.querySelectorAll('[data-mobile-menu-open]');
    this.accordionTriggers = this.querySelectorAll('[data-mobile-menu-accordion-trigger]');

    if (!this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');

    this.openTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-controls', this.drawer.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Open menu');
      trigger.addEventListener('click', this.open);
    });

    this.closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.close);
    });

    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onAccordionTriggerClick);
      this.setAccordionState(trigger, trigger.getAttribute('aria-expanded') === 'true');
    });

    window.addEventListener('resize', this.onResize);
    this.desktopQuery.addEventListener('change', this.onDesktopChange);
  }

  disconnectedCallback() {
    this.desktopQuery.removeEventListener('change', this.onDesktopChange);

    this.openTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.open);
    });

    this.closeTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.close);
    });

    this.accordionTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.onAccordionTriggerClick);
    });

    this.header?.classList.remove('is-menu-open');
    this.resetHeaderColorState();
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
  }

  open(event) {
    event?.preventDefault();

    if (this.isOpen) {
      this.close(event);
      return;
    }

    this.cancelHeaderColorReset();
    this.isOpen = true;
    this.openTrigger = event?.currentTarget || null;
    this.previousOverflow = document.documentElement.style.overflow;

    this.drawer.classList.add('show-menu');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.removeAttribute('inert');
    this.overlay?.classList.remove('hidden-overlay');
    this.setHeaderMenuState();
    this.syncOpenAccordionHeights();
    document.documentElement.style.overflow = 'hidden';

    this.openTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-label', 'Close menu');
    });

    document.addEventListener('keydown', this.onKeyDown);
    this.focusFirstElement();
  }

  close(event) {
    event?.preventDefault();

    if (!this.isOpen) return;

    this.isOpen = false;

    this.drawer.classList.remove('show-menu');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    this.overlay?.classList.add('hidden-overlay');
    this.header?.classList.remove('is-menu-open');
    this.scheduleHeaderColorReset();
    document.documentElement.style.overflow = this.previousOverflow;

    this.openTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Open menu');
    });

    document.removeEventListener('keydown', this.onKeyDown);
    this.openTrigger?.focus();
  }

  onAccordionTriggerClick(event) {
    const trigger = event.currentTarget;
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    this.setAccordionState(trigger, !isExpanded);
  }

  setAccordionState(trigger, isExpanded) {
    const accordion = trigger.closest('[data-mobile-menu-accordion]');
    const panel = accordion?.querySelector('[data-mobile-menu-accordion-panel]');
    const inner = panel?.querySelector('[data-mobile-menu-accordion-inner]');

    if (!panel || !inner) return;

    trigger.setAttribute('aria-expanded', String(isExpanded));
    panel.setAttribute('aria-hidden', String(!isExpanded));
    accordion.classList.toggle('is-open', isExpanded);

    if (isExpanded) {
      panel.removeAttribute('inert');
      panel.style.minHeight = `${inner.scrollHeight}px`;
      return;
    }

    panel.setAttribute('inert', '');
    panel.style.minHeight = '0px';
  }

  syncOpenAccordionHeights() {
    this.accordionTriggers?.forEach((trigger) => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.setAccordionState(trigger, true);
      }
    });
  }

  onResize() {
    this.syncOpenAccordionHeights();
  }

  onDesktopChange(event) {
    if (event.matches && this.isOpen) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close(event);
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements();

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return;
    }

    this.drawer.focus();
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements();

    if (focusableElements.length === 0) {
      event.preventDefault();
      this.drawer.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  getFocusableElements() {
    return Array.from(
      this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
  }

  setHeaderMenuState() {
    if (!this.header) return;

    const isAtTop = window.scrollY <= 8;

    this.header.classList.add('is-menu-open');
    this.header.classList.toggle('is-menu-open-at-top', isAtTop);

    if (isAtTop) {
      this.header.style.setProperty('--mobile-menu-background', getComputedStyle(this.drawer).backgroundColor);
      return;
    }

    this.header.style.removeProperty('--mobile-menu-background');
  }

  scheduleHeaderColorReset() {
    if (!this.header) return;

    if (!this.header.classList.contains('is-menu-open-at-top')) {
      this.resetHeaderColorState();
      return;
    }

    this.cancelHeaderColorReset();
    this.headerColorResetTimeout = window.setTimeout(() => {
      this.resetHeaderColorState();
    }, 400);
  }

  cancelHeaderColorReset() {
    if (!this.headerColorResetTimeout) return;

    window.clearTimeout(this.headerColorResetTimeout);
    this.headerColorResetTimeout = null;
  }

  resetHeaderColorState() {
    this.cancelHeaderColorReset();
    this.header?.classList.remove('is-menu-open-at-top');
    this.header?.style.removeProperty('--mobile-menu-background');
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
